import React from 'react';
import Cookies from 'universal-cookie';
import { LoaderCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import cwtLogo from './images/logo.png';

const cookies = new Cookies();
const POLL_INTERVAL_MS = 2500;

function joinUrl(baseUrl, path) {
    return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function getInitialFilter() {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    const timeFrom = new Date(now);
    timeFrom.setHours(now.getHours() - 1);

    return {
        sensor: '',
        timeFrom: timeFrom.toISOString().slice(0, -5),
        timeTo: now.toISOString().slice(0, -5),
    };
}

function formatDateTime(value) {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString('en-SG', {
        dateStyle: 'medium',
        timeStyle: 'short',
        hour12: false,
        timeZone: 'Asia/Singapore',
    });
}

function formatNumber(value, decimals = 2) {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const number = Number(value);
    if (Number.isNaN(number)) {
        return value;
    }

    return number.toFixed(decimals);
}

function getAnalysisText(job) {
    return job?.ai_result?.analysis || {};
}

function getList(value) {
    return Array.isArray(value) ? value.filter(Boolean) : [];
}

function loadImageDataUrl(src) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;

            const context = canvas.getContext('2d');
            if (!context) {
                reject(new Error('Could not create canvas context.'));
                return;
            }

            context.drawImage(image, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        image.onerror = () => reject(new Error(`Could not load image: ${src}`));
        image.src = src;
    });
}

async function drawReportHeader(pdf, title, margin) {
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFontSize(18);
    pdf.setTextColor(15, 23, 42);
    pdf.text(title, margin, 39);

    try {
        const [cwtLogoData, tioLogoData] = await Promise.all([
            loadImageDataUrl(cwtLogo),
            loadImageDataUrl(`${process.env.PUBLIC_URL}/tio-logo.png`),
        ]);

        const right = pageWidth - margin;
        const top = 19;
        const tioSize = 32;
        const cwtWidth = 58;
        const cwtHeight = 32;
        const separatorX = right - tioSize - 10;

        pdf.addImage(tioLogoData, 'PNG', right - tioSize, top, tioSize, tioSize);
        pdf.setDrawColor(203, 213, 225);
        pdf.setLineWidth(0.6);
        pdf.line(separatorX, top, separatorX, top + tioSize);
        pdf.addImage(
            cwtLogoData,
            'PNG',
            separatorX - cwtWidth - 10,
            top + (tioSize - cwtHeight) / 2,
            cwtWidth,
            cwtHeight
        );
    } catch (error) {
        console.log('error loading report header logos', error);
    }

    pdf.setDrawColor(226, 232, 240);
    pdf.setLineWidth(0.8);
    pdf.line(margin, 56, pageWidth - margin, 56);
}

function AnalysisSection({ title, children, className = '' }) {
    return (
        <section className={`analysis-section ${className}`.trim()}>
            <h3>{title}</h3>
            {children}
        </section>
    );
}

function TextList({ items, emptyText }) {
    const list = getList(items);

    if (!list.length) {
        return <p className="analysis-muted">{emptyText}</p>;
    }

    return (
        <ul className="analysis-list">
            {list.map((item, index) => (
                <li key={`${item}-${index}`}>{item}</li>
            ))}
        </ul>
    );
}

function Metric({ label, value, detail }) {
    return (
        <div className="analysis-metric">
            <span>{label}</span>
            <strong>{value}</strong>
            {detail ? <p>{detail}</p> : null}
        </div>
    );
}

function CompletedAnalysis({ job }) {
    const analysis = getAnalysisText(job);
    const statistics = job.statistics || {};
    const extremes = job.extremes || {};
    const metadata = job.payload_metadata || {};
    const unit = '\u00b0C';

    return (
        <div className="analysis-complete">
            <div className="analysis-header">
                <div>
                    <h2>{metadata.sensor_name || metadata.sensor_id || job.sensor_id || 'Selected sensor'}</h2>
                    <p>
                        {formatDateTime(metadata.start_time)} to {formatDateTime(metadata.end_time)}
                    </p>
                </div>
                <span className="analysis-badge">Completed</span>
            </div>

            <AnalysisSection title="Summary" className="analysis-section-summary">
                <p className="analysis-summary">{analysis.summary || 'No summary was returned.'}</p>
            </AnalysisSection>

            <AnalysisSection title="Trend" className="analysis-section-trend">
                <p>{analysis.trend || 'No trend was returned.'}</p>
            </AnalysisSection>

            <AnalysisSection title="Temperature Statistics" className="analysis-section-wide">
                <div className="analysis-metrics-grid">
                    <Metric
                        label="Minimum"
                        value={`${formatNumber(statistics.min)} ${unit}`}
                        detail={formatDateTime(extremes.min?.timestamp)}
                    />
                    <Metric
                        label="Maximum"
                        value={`${formatNumber(statistics.max)} ${unit}`}
                        detail={formatDateTime(extremes.max?.timestamp)}
                    />
                    <Metric label="Average" value={`${formatNumber(statistics.average)} ${unit}`} />
                    <Metric label="Median" value={`${formatNumber(statistics.median)} ${unit}`} />
                    <Metric label="Std deviation" value={`${formatNumber(statistics.std_dev)} ${unit}`} />
                    <Metric label="95 Percentile" value={`${formatNumber(statistics.p95)} ${unit}`} />
                    <Metric label="97 Percentile" value={`${formatNumber(statistics.p97)} ${unit}`} />
                </div>
            </AnalysisSection>

            <AnalysisSection title="Key Findings" className="analysis-section-findings">
                <TextList items={analysis.key_findings} emptyText="No key findings were returned." />
            </AnalysisSection>

            <AnalysisSection title="Unusual Observations" className="analysis-section-observations">
                <TextList items={analysis.unusual_observations} emptyText="No unusual observations were returned." />
            </AnalysisSection>

            <AnalysisSection title="Data Quality" className="analysis-section-quality">
                <TextList items={analysis.data_quality_notes} emptyText="No data quality notes were returned." />
            </AnalysisSection>

            <AnalysisSection title="Recommendations" className="analysis-section-recommendations">
                <TextList items={analysis.recommendations} emptyText="No recommendations were returned." />
            </AnalysisSection>
        </div>
    );
}

function Analysis(props) {
    const [filter, setFilter] = React.useState(getInitialFilter);
    const [job, setJob] = React.useState(null);
    const [status, setStatus] = React.useState('');
    const [isAnalysing, setIsAnalysing] = React.useState(false);
    const pollTimerRef = React.useRef(null);
    const analysisResultRef = React.useRef(null);

    function clearPolling() {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }

    React.useEffect(() => clearPolling, []);

    function makeChange(event) {
        const { name, value } = event.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    }

    async function readResponse(response) {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const message = data.message || data.error || 'Request failed.';
            const error = new Error(message);
            error.statusCode = response.status;
            throw error;
        }
        return data;
    }

    async function fetchJob(nextJobId) {
        const response = await fetch(joinUrl(props.config.analysis_backend_url, `analysis-job/${nextJobId}`), {
            headers: {
                'Authorization': cookies.get('idToken')
            }
        });
        return readResponse(response);
    }

    async function pollJob(nextJobId) {
        try {
            const nextJob = await fetchJob(nextJobId);
            setJob(nextJob);

            if (nextJob.status === 'done') {
                clearPolling();
                setIsAnalysing(false);
                setStatus('');
                return false;
            }

            if (nextJob.status === 'failed') {
                clearPolling();
                setIsAnalysing(false);
                setStatus(nextJob.error_message || 'AI analysis failed. Please try again.');
                return false;
            }

            setStatus('AI analysis is still running...');
            return true;
        } catch (error) {
            clearPolling();
            setIsAnalysing(false);
            if (error.statusCode === 400) {
                setStatus(`AI analysis failed: ${error.message}`);
            } else {
                setStatus(error.message || 'Unable to check AI analysis status.');
            }
            return false;
        }
    }

    async function handleAnalyse() {
        if (!filter.sensor) {
            setStatus('Please select a sensor first.');
            return;
        }

        if (!filter.timeFrom || !filter.timeTo) {
            setStatus('Please select both start and end timestamps.');
            return;
        }

        if (new Date(filter.timeFrom) > new Date(filter.timeTo)) {
            setStatus('Start timestamp must be before end timestamp.');
            return;
        }

        clearPolling();
        setIsAnalysing(true);
        setJob(null);
        setStatus('Starting AI analysis job...');

        try {
            const response = await fetch(joinUrl(props.config.analysis_backend_url, 'analysis-job'), {
                method: 'POST',
                headers: {
                    'Authorization': cookies.get('idToken'),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sensor_id: filter.sensor,
                    time_from: new Date(filter.timeFrom).toISOString(),
                    time_to: new Date(filter.timeTo).toISOString(),
                }),
            });
            const startedJob = await readResponse(response);
            const nextJobId = startedJob.job_id;

            if (!nextJobId) {
                throw new Error('Backend did not return a job id.');
            }

            setStatus('AI analysis started. Checking for results...');
            const shouldContinuePolling = await pollJob(nextJobId);
            if (shouldContinuePolling) {
                pollTimerRef.current = setInterval(() => {
                    pollJob(nextJobId);
                }, POLL_INTERVAL_MS);
            }
        } catch (error) {
            clearPolling();
            setIsAnalysing(false);
            setStatus(error.message || 'Unable to start AI analysis. Please try again.');
        }
    }

    function handleDownloadPDF() {
        const content = analysisResultRef.current;
        if (!content || job?.status !== 'done') {
            setStatus('Run an analysis before exporting.');
            return;
        }

        html2canvas(content, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
        }).then(async (canvas) => {
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            const pdf = new jsPDF('portrait', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 24;
            const contentTop = 72;
            const pageContentHeight = pageHeight - contentTop - margin;
            const imgWidth = pageWidth - margin * 2;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let renderedHeight = 0;
            let pageIndex = 0;

            while (renderedHeight < imgHeight) {
                if (pageIndex > 0) {
                    pdf.addPage();
                }

                const position = contentTop - renderedHeight;
                pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
                await drawReportHeader(pdf, 'AI Analysis', margin);

                renderedHeight += pageContentHeight;
                pageIndex += 1;
            }

            const sensorLabel = (job.payload_metadata?.sensor_name || job.sensor_id || 'analysis')
                .replace(/[^a-z0-9-_]+/gi, '_')
                .replace(/^_+|_+$/g, '');
            pdf.save(`${sensorLabel || 'analysis'}_analysis.pdf`);
        }).catch((error) => {
            console.log('error exporting analysis pdf', error);
            setStatus('Unable to export analysis PDF. Please try again.');
        });
    }

    return (
        <div className="main analysis-page">
            <div className="analysis-controls">
                <select
                    id="analysisSensor"
                    name="sensor"
                    value={filter.sensor}
                    onChange={makeChange}
                >
                    <option value="" disabled>- select sensor -</option>
                    {props.sensorList ? (
                        Object.entries(props.sensorList).map(([key, value]) => (
                            <option value={key} key={key}>
                                {value}
                            </option>
                        ))
                    ) : []}
                </select>

                <input
                    type="datetime-local"
                    id="analysisTimeFrom"
                    name="timeFrom"
                    value={filter.timeFrom}
                    onChange={makeChange}
                />

                <span>-</span>

                <input
                    type="datetime-local"
                    id="analysisTimeTo"
                    name="timeTo"
                    min={filter.timeFrom.slice(0, -3)}
                    value={filter.timeTo}
                    onChange={makeChange}
                />

                <button onClick={handleAnalyse} disabled={isAnalysing}>
                    {isAnalysing ? 'AI Analysing...' : 'AI Analysis'}
                </button>

                <button onClick={handleDownloadPDF} disabled={job?.status !== 'done'}>
                    Export PDF
                </button>
            </div>

            <div className="analysis-results">
                {status ? (
                    <div className="analysis-status">
                        {isAnalysing ? <LoaderCircle className="analysis-loading-icon" size={20} /> : null}
                        <span>{status}</span>
                    </div>
                ) : null}

                {job?.status === 'done' ? (
                    <div ref={analysisResultRef}>
                        <CompletedAnalysis job={job} />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default Analysis;
